import type { EreignisStatus, MassnahmenStatus, RisikoStatus } from "@iso27001/shared";

// The terminal states of each register. An entry in one of them is finished
// business — a closed event, a measure that was carried out or dropped, a risk
// that no longer exists — so its dialog opens read-only, and changing it takes
// a deliberate "Bearbeiten" first (see useRecordAccess). Akzeptiert is not
// terminal: an accepted risk is still a live risk that gets reviewed.
export const isEreignisClosed = (s: EreignisStatus): boolean => s === "Geschlossen";
export const isMassnahmeClosed = (s: MassnahmenStatus): boolean => s === "Erledigt" || s === "Verworfen";
export const isRisikoClosed = (s: RisikoStatus): boolean => s === "Geschlossen";

export interface RecordAccess {
    /** Whether the current user may change records at all (editor or admin). */
    canEdit: boolean;
    /** Whether the dialog shows the record without letting it be changed. */
    readOnly: boolean;
    /** The record is in a terminal state — the dialog says so. */
    closed: boolean;
    /** Switches a closed record to editing; absent when that isn't allowed. */
    unlock?: () => void;
}

/**
 * Viewers always see records read-only. Editors see closed records read-only
 * until they unlock them, so that reading a finished entry cannot change it
 * by accident. New records are never closed.
 */
export function resolveRecordAccess(role: string | undefined, closed: boolean, unlocked: boolean): Omit<RecordAccess, "unlock"> & { canUnlock: boolean } {
    const canEdit = role === "admin" || role === "editor";
    const readOnly = !canEdit || (closed && !unlocked);
    return { canEdit, readOnly, closed, canUnlock: canEdit && readOnly };
}
